/**
 * Derived milk — "milk is part of the meal" on the school order sheet.
 *
 * The menu's system-materialized standalone milk rows (`lineupMilkOptionCode`
 * set — see `menu-milk-materializer.ts`) stop being hand-typed: their DEFAULT
 * quantities derive live from the meal counts the school enters —
 *
 *   milkTotal(column, mealtime) = ceil(mealTotal × BEVERAGE delivery rate)
 *   per-SKU quantity            = milkTotal apportioned by the school's milk
 *                                 shares (largest-remainder; even split when
 *                                 no shares are configured)
 *
 * mirroring `DeliveryRateResolver.expandShape`'s ceil and the
 * `MilkShareResolver` split, so what the sheet suggests is exactly what the
 * server would compute. The derived lines carry `suggested: true` — dashed
 * inputs that keep tracking the meal counts until the school touches a cell,
 * at which point their own value wins (the sync never overwrites a
 * non-suggested line). The submit path is untouched: milk still ships, is
 * paid, and settles as its own order line at the exact quantity in the cell —
 * "part of the bundle, cost paid out separately" (Sheila, 2026-07-29).
 *
 * EVERY order column derives — Straight Serve included (Sheila, 2026-08-24:
 * OLHSA's CACFP sheet showed "Bagel • Applesauce • Milk" next to two
 * standalone orderable milk rows, i.e. milk twice). An SS column simply
 * derives at its BEVERAGE rate's default — missing rate row = 100%, one
 * carton per meal — and an even SKU split when no shares are configured,
 * exactly what the server-side `DerivedMilkReconciler` computes for
 * parent-order campuses, which has never had a service-style gate. See
 * `milkDerivationColumns`.
 */
import { milkTotalForMeals } from '@ordo/constants/milk-derivation'

import type { ComponentKind } from '@/api/location-settings'
import type { Mealtime } from '@/lib/mealtime'

import { cartLineKey, type SchoolCartLine } from './cart'
import { isItemAllowedForColumn, type OrderColumn } from './columns'
import { apportionQuantity } from './milk'
import type { PrefillMenuItem } from './prefill'

/** Pre-normalized item shape for the derivation (superset of prefill's). */
export interface DerivedMilkMenuItem extends PrefillMenuItem {
  /** The milk SKU's Component id — keys the milk-share lookup. */
  skuComponentId: string | null
  /**
   * Meal-column bucket id when the item carries meal-column provenance
   * (composed BUNDLE meals and instance-added extras in a bucket's column).
   * Lets a moved meal's component items count as meals for milk derivation
   * when no composed bundle exists for that bucket.
   */
  mealColumnBucketId?: string | null
}

/**
 * Where the derivation reads MEAL COUNTS from.
 *
 * Two callers, two stores: a DRAFT day's counts live in the draft store
 * (`draftMap`), while a PLACED day being edited holds them in the panel's own
 * edit buffer. Before this was parameterized the derivation read `draftMap`
 * unconditionally, so it could only ever serve draft mode — which is why
 * removing the placed-day guard on its own derived from zero meals and wrote
 * zeros. Pass exactly one; `mealQtyFor` wins if both are given.
 */
export type MealQuantitySource =
  | { draftMap: Record<string, SchoolCartLine>; mealQtyFor?: never }
  | { mealQtyFor: (menuItemId: string, columnId: string) => number; draftMap?: never }

export interface DeriveMilkLinesBase {
  locationId: string
  dateKey: string
  mealtime: Mealtime
  /** Every visible item of the mealtime (meals + milk + à-la-carte). */
  items: readonly DerivedMilkMenuItem[]
  /** Order columns that may derive (the campus's OVS columns only). */
  columns: readonly OrderColumn[]
  /** Delivery-rate resolver — bps per kind per (mealtime, column). */
  ratesFor: (mealtime: Mealtime | null, columnId: string) => Partial<Record<ComponentKind, number>>
  /** Milk share % per SKU Component id per (mealtime, column). */
  milkShareFor: (mealtime: Mealtime | null, columnId: string) => Record<string, number>
}

export type DeriveMilkLinesInput = DeriveMilkLinesBase & MealQuantitySource

/**
 * Resolve a `MealQuantitySource` to one `(menuItemId) => qty` lookup.
 *
 * The union type makes passing NEITHER source a compile error — a silent
 * fallback to "no counts" would derive zeros, which is precisely the bug this
 * parameterization exists to prevent.
 */
function mealQuantityLookup(
  counts: MealQuantitySource,
  dateKey: string,
  mealtime: Mealtime,
  columnId: string,
): (menuItemId: string) => number {
  if (counts.mealQtyFor) {
    const lookup = counts.mealQtyFor
    return (menuItemId) => Math.max(0, lookup(menuItemId, columnId))
  }
  const draftMap = counts.draftMap ?? {}
  return (menuItemId) => {
    const line = draftMap[cartLineKey(dateKey, mealtime, columnId, menuItemId)]
    return line && line.quantity > 0 ? line.quantity : 0
  }
}

/** The menu's system milk rows — the only rows this module derives. */
export function lineupMilkItems<T extends { lineupMilkOptionCode: string | null }>(items: readonly T[]): T[] {
  return items.filter((it) => it.lineupMilkOptionCode != null)
}

/**
 * Whether a mealtime's system milk rides inside the meal on the SCHOOL order
 * sheet: whenever the mealtime has system milk rows at all. This is the gate
 * for hiding those rows from the orderable item list AND for running the
 * derivation — the two must always flip together, or milk either shows twice
 * (visible rows + derived lines) or stops shipping (hidden rows, no lines).
 *
 * Deliberately NOT conditioned on the campus having OVS columns. That
 * condition made Straight Serve campuses render system milk as plain
 * hand-entered order rows next to bundles already named "… • Milk" — read as
 * milk twice, and shipped NO milk unless the school retyped its meal counts
 * into them (OLHSA, 2026-08-24). À-la-carte milk menus are untouched: their
 * rows are human-placed (`lineupMilkOptionCode: null`) and never match.
 */
export function milkDerivationActive(items: readonly { lineupMilkOptionCode: string | null }[]): boolean {
  return lineupMilkItems(items).length > 0
}

/**
 * The order columns a mealtime's system milk derives on: ALL of them.
 *
 * Straight Serve columns are NOT filtered out (they were until 2026-08-24):
 * an SS column derives at its BEVERAGE delivery rate exactly like an OVS
 * one — the default with no configured rate row is 100%, one carton per
 * meal, which is precisely "Straight Serve ships 100% of every component".
 * This mirrors the server-side `DerivedMilkReconciler` (parent-order
 * campuses), which applies `DeliveryRateResolver`/`MilkShareResolver` with
 * no service-style gate.
 */
export function milkDerivationColumns(columns: readonly OrderColumn[]): OrderColumn[] {
  return [...columns]
}

/**
 * Total meals entered for a (column, mealtime): bundle-item draft quantities,
 * suggested or not (history-prefilled meals still drink milk). À-la-carte
 * components — including the milk rows themselves — never count as meals.
 */
export function mealTotalFor(
  items: readonly DerivedMilkMenuItem[],
  counts: MealQuantitySource,
  dateKey: string,
  mealtime: Mealtime,
  columnId: string,
): number {
  // A meal = one bucket-day serving (Sheila, 2026-08-07): whatever is
  // slotted in a bucket for the day is ONE meal, however many item rows
  // compose it. Composed BUNDLE rows are that serving directly. A bucket
  // with NO bundle (the school moved/rebuilt the meal as individual
  // meal-column items) counts as MAX of its item quantities — 100 chicken +
  // 100 mac + 100 veggie in Daily Special is 100 meals, not 300 — and
  // extras added next to a still-composed bundle never count again.
  const bundleBuckets = new Set(
    items.flatMap((it) => (it.itemKind === 'BUNDLE' && it.mealColumnBucketId ? [it.mealColumnBucketId] : [])),
  )
  const lookup = mealQuantityLookup(counts, dateKey, mealtime, columnId)
  const quantityFor = (it: DerivedMilkMenuItem): number => lookup(it.id)
  let total = 0
  const bucketMax = new Map<string, number>()
  for (const it of items) {
    if (it.itemKind === 'BUNDLE') {
      total += quantityFor(it)
      continue
    }
    const bucketId = it.mealColumnBucketId
    if (bucketId && !bundleBuckets.has(bucketId)) {
      bucketMax.set(bucketId, Math.max(bucketMax.get(bucketId) ?? 0, quantityFor(it)))
    }
  }
  for (const quantity of bucketMax.values()) {
    total += quantity
  }
  return total
}

/**
 * One DISPLAYED milk row: a milk SKU plus every system milk MenuItem that
 * carries it for this mealtime.
 *
 * A campus can run two menus at the same mealtime (Treasure Coast: "Lunch
 * Menu (K-8)" + "Lunch (9-12)"), and the materializer places one system row
 * per lineup code PER MENU — so the same carton of 1% milk arrives as two
 * menu items and used to render as two identical rows, each holding half the
 * derived quantity. The SKU is the unit the school thinks in ("how much 1%
 * do we want?"), so rows collapse by `skuComponentId`; the per-menu items
 * stay the ship/pay carriers underneath.
 */
export interface MilkSkuRow {
  /** Stable row key: the SKU's Component id (an unstamped row keys by item id). */
  key: string
  name: string
  skuComponentId: string | null
  /** The contributing system menu items, in menu order. */
  items: DerivedMilkMenuItem[]
}

/** Group this mealtime's system milk rows into one displayed row per SKU. */
export function groupMilkRowsBySku(rows: readonly DerivedMilkMenuItem[]): MilkSkuRow[] {
  const byKey = new Map<string, MilkSkuRow>()
  for (const row of rows) {
    const key = row.skuComponentId ?? row.id
    const existing = byKey.get(key)
    if (existing) {
      existing.items.push(row)
      continue
    }
    byKey.set(key, { key, name: row.name, skuComponentId: row.skuComponentId, items: [row] })
  }
  return [...byKey.values()]
}

/**
 * The menu item a collapsed row writes to in one column — the first member
 * the column gate admits (menu grade band / field-trip scope). Null when the
 * column serves none of them, which is exactly when the grid dashes the cell
 * out. Bands don't normally overlap, so this is a genuine 1-of-1 pick; when
 * they do, the first member (menu order) carries the whole quantity and its
 * siblings are zeroed, so one number never ships twice.
 */
export function milkCarrierForColumn(row: MilkSkuRow, column: OrderColumn): DerivedMilkMenuItem | null {
  return row.items.find((item) => !item.columnScope || isItemAllowedForColumn(item.columnScope, column)) ?? null
}

/** Every member of a collapsed row the column admits (carrier first). */
export function milkItemsForColumn(row: MilkSkuRow, column: OrderColumn): DerivedMilkMenuItem[] {
  return row.items.filter((item) => !item.columnScope || isItemAllowedForColumn(item.columnScope, column))
}

/**
 * The derived milk lines for one mealtime — one per (milk SKU × column) the
 * grid actually renders, INCLUDING zero-quantity lines: the sync deletes a
 * still-suggested line whose derivation dropped to 0 (e.g. the school
 * cleared its meals), so tombstones are part of the contract. A SKU that
 * arrives on more than one of the mealtime's menus derives ONCE at its full
 * share, onto that column's carrier item; the other carriers of the same SKU
 * get a 0 line so a stale suggestion can't double the milk.
 */
export function deriveMilkLines(input: DeriveMilkLinesInput): SchoolCartLine[] {
  // The union makes a missing count source a compile error; this keeps it an
  // error for callers the compiler never sees (JS, a cast, JSON-parsed
  // input). Defaulting to "no counts" would derive zero milks for every line
  // and silently strip milk from an order — the exact bug this
  // parameterization exists to prevent. Checked at the boundary so an empty
  // items/columns list cannot skip it.
  if (!input.mealQtyFor && !input.draftMap) {
    throw new Error('deriveMilkLines: a meal-quantity source is required (pass draftMap or mealQtyFor)')
  }
  const milkRows = lineupMilkItems(input.items)
  if (milkRows.length === 0 || input.columns.length === 0) {
    return []
  }
  const skuRows = groupMilkRowsBySku(milkRows)
  const lines: SchoolCartLine[] = []
  for (const column of input.columns) {
    // Never derive into a cell the grid dashes out (menu grade band /
    // field-trip scope) — same gate as history prefill.
    const rows = skuRows.filter((row) => milkCarrierForColumn(row, column) !== null)
    if (rows.length === 0) {
      continue
    }
    const mealTotal = mealTotalFor(
      input.items,
      input.mealQtyFor ? { mealQtyFor: input.mealQtyFor } : ({ draftMap: input.draftMap } as MealQuantitySource),
      input.dateKey,
      input.mealtime,
      column.columnId,
    )
    // Mirror DeliveryRateResolver.expandShape: missing rate row = 100%,
    // over-prep beats under-prep (ceil) — the shared `milkTotalForMeals`,
    // which the server-side field-trip milk placer runs too.
    const milkTotal = milkTotalForMeals(mealTotal, input.ratesFor(input.mealtime, column.columnId)['BEVERAGE'])
    // Shares are keyed by SKU Component id; rows without a share fall back
    // to an even weight so the apportionment still sums to milkTotal.
    const sharePct = input.milkShareFor(input.mealtime, column.columnId)
    const shareByRow: Record<string, number> = {}
    for (const row of rows) {
      shareByRow[row.key] = (row.skuComponentId ? sharePct[row.skuComponentId] : undefined) ?? 100 / rows.length
    }
    const qtyByRow = apportionQuantity(milkTotal, shareByRow)
    for (const row of rows) {
      // Carrier first, then the same SKU's other menu items in this column
      // as 0 tombstones.
      milkItemsForColumn(row, column).forEach((item, index) => {
        lines.push({
          key: cartLineKey(input.dateKey, input.mealtime, column.columnId, item.id),
          locationId: input.locationId,
          date: input.dateKey,
          mealtime: input.mealtime,
          columnId: column.columnId,
          gradeRangeId: column.gradeRangeId,
          groupDimension: column.groupDimension,
          groupValue: column.groupValue,
          menuItemId: item.id,
          name: item.name,
          imageUri: item.imageUri,
          unitPriceCents: item.unitPriceCents,
          currency: item.currency,
          quantity: index === 0 ? (qtyByRow[row.key] ?? 0) : 0,
          suggested: true,
          systemMilk: true,
        })
      })
    }
  }
  return lines
}

// ---------------------------------------------------------------------------
// Placed-day Edit: which milk cells keep deriving
// ---------------------------------------------------------------------------

/** The placed order as the Edit seed reads it, one column audience at a time. */
export interface PlacedMilkCells {
  /** Placed quantity for (mealtime, column audience, menu item); 0 when nothing is placed there. */
  qty(mealtime: Mealtime, audienceId: string, menuItemId: string): number
  /** True when the placed line's OWN quantity is system-DERIVED (`AlreadyOrdered.cellQtyIsDerived`). */
  isDerived(mealtime: Mealtime, audienceId: string, menuItemId: string): boolean
}

export interface EditDerivedMilkKeysInput {
  dateKey: string
  /** Browse-grid items per mealtime; only the system milk rows (`lineupMilkOptionCode`) are considered. */
  itemsByMealtime: ReadonlyMap<Mealtime, readonly DerivedMilkMenuItem[]>
  /** Field-trip extra rows per mealtime; only the bagged menus' milk carriers (`systemMilk`) are considered. */
  extraRowsByMealtime: ReadonlyMap<Mealtime, readonly { menuItemId: string; systemMilk: boolean }[]>
  columnsFor: (mealtime: Mealtime) => readonly OrderColumn[]
  placed: PlacedMilkCells
}

/**
 * The system-milk cells of a PLACED day that keep deriving inside the Edit
 * buffer — the keys `editDerivedKeys` starts from. A cell derives when:
 *
 *   - its placed line is DERIVED: the count still tracks the meal counts, so
 *     a rate or meal change reaches it without the school retyping anything;
 *   - the day never placed it at all: no number of the school's lives there.
 *
 * A placed line that is NOT derived is the school's own number and is never
 * touched. ONE rule for the hot menus' milk rows and the bagged menus'
 * carriers (PROD-1244), and it has to be: a day whose batch the field-trip
 * placer created first (bagged lines only) opens in Edit, and Edit derives
 * nothing but the keys seeded here. While only the carriers got the
 * "never placed" half, the hot menus' milk cells never derived, and the
 * meals typed beside a field trip submitted with NO milk — Amana Academy,
 * Sep 14 + 16 2026: 110 breakfasts and 150 hot lunches a day, 0 cartons;
 * Howard Street and Brookside the same way. A never-placed cell and a
 * hand-typed 0 are the same absence of a line, so a school that wants no
 * milk retypes 0 in Edit (typing a cell drops it from the derivation).
 *
 * Cells the grid dashes out (menu grade band / field-trip scope) are never
 * seeded, mirroring the derivation's own column gate.
 */
export function editDerivedMilkKeys(input: EditDerivedMilkKeysInput): Set<string> {
  const keys = new Set<string>()
  const consider = (mealtime: Mealtime, menuItemId: string, column: OrderColumn): void => {
    const placedQty = input.placed.qty(mealtime, column.audienceId, menuItemId)
    if (placedQty === 0 || input.placed.isDerived(mealtime, column.audienceId, menuItemId)) {
      keys.add(cartLineKey(input.dateKey, mealtime, column.columnId, menuItemId))
    }
  }
  for (const [mealtime, rows] of input.extraRowsByMealtime) {
    for (const row of rows) {
      if (!row.systemMilk) {
        continue
      }
      for (const column of input.columnsFor(mealtime)) {
        consider(mealtime, row.menuItemId, column)
      }
    }
  }
  for (const [mealtime, items] of input.itemsByMealtime) {
    for (const item of lineupMilkItems(items)) {
      for (const column of input.columnsFor(mealtime)) {
        if (item.columnScope && !isItemAllowedForColumn(item.columnScope, column)) {
          continue
        }
        consider(mealtime, item.id, column)
      }
    }
  }
  return keys
}
